import React from 'react'
import Link from 'next/link'
import { FaRegLightbulb, FaArrowRight } from 'react-icons/fa'

const base = '/sg/aircon-servicing-singapore'

export const symptomMatches = [
    {
        symptom: 'Nothing is wrong, the units are just due for upkeep',
        service: 'General Service',
        href: `${base}/cleaning-maintenance-service`,
        reason: 'Filters, cover, evaporator coil and drain pan are cleaned, the drainage pipe is vacuumed and gas pressure is checked.',
    },
    {
        symptom: 'Weak airflow or a musty smell even after a normal service',
        service: 'Chemical Wash',
        href: `${base}/aircon-chemical-wash-and-overhaul`,
        reason: 'Dirt has bonded to the evaporator coil. A surface clean will not shift it, so the coil needs chemical treatment.',
    },
    {
        symptom: 'Still cooling poorly after a recent service, or unit unused for months',
        service: 'Chemical Overhaul',
        href: `${base}/aircon-chemical-wash-and-overhaul`,
        reason: 'The fan coil unit comes off the wall so the blower wheel, coil and full drainage line can be cleared.',
    },
    {
        symptom: 'Water dripping or leaking from the indoor unit',
        service: 'Repair — drainage',
        href: `${base}/aircon-leaking-water`,
        reason: 'Almost always a choked drain line or dirty filters, not a refrigerant problem. Flushing the drainage usually fixes it.',
    },
    {
        symptom: 'Water pooling or a gurgling sound from the drain line',
        service: 'Drainage pipe service',
        href: `${base}/aircon-drainage-pipe`,
        reason: 'Sludge and algae build up inside the drainage pipe over time. Flushing it clears the blockage at source.',
    },
    {
        symptom: 'Blowing air but it is not cold',
        service: 'Book a diagnostic first',
        href: `${base}/aircon-not-cooling`,
        reason: 'The cause could be low refrigerant, a dirty coil, a failing capacitor or the PCB. We check before quoting any parts.',
    },
    {
        symptom: 'Rattling, buzzing or grinding noises',
        service: 'Repair — mechanical',
        href: `${base}/top-3-reasons-for-noisy-aircon-compressor-how-to-fix`,
        reason: 'Usually the blower, indoor fan motor or a loose compressor mounting rather than the refrigerant circuit.',
    },
    {
        symptom: 'Unit runs hot or shuts off after a while',
        service: 'Overheating check',
        href: `${base}/aircon-overheating`,
        reason: 'Clogged filters and coils trap heat, so the unit cuts out on thermal protection after long run hours.',
    },
    {
        symptom: 'Outdoor unit runs hot, is noisy or trips the breaker',
        service: 'Compressor Cleaning',
        href: `${base}/aircon-condenser-cleaning`,
        reason: 'A condenser cannot reject heat when its fins are blocked, which makes the compressor overwork and trip.',
    },
    {
        symptom: 'Unit will not power on or ignores the remote',
        service: 'PCB Replacement',
        href: `${base}/ac-printed-circuit-board-pcb`,
        reason: 'The PCB drives the fan coil and compressor. When it fails the system will not respond at all.',
    },
    {
        symptom: 'A technician has already confirmed the refrigerant is low',
        service: 'Gas Top-Up',
        href: `${base}/aircon-gas-top-up`,
        reason: 'Refrigerant does not get consumed, so we locate the leak first. Refilling without fixing the leak wastes your money.',
    },
]

// UAE / Malaysia pass `rows` [{ symptom, service, href, reason }] and their own
// title, intro, caption and footnote; Singapore passes none.
export const AirconServiceMatcher = ({ rows = symptomMatches, title, intro, caption, footnote } = {}) => {
    return (
        <section className="py-8 md:py-14" aria-labelledby="aircon-service-matcher">
            <div className="max-w-5xl mx-auto px-4">
                <div className="text-center mb-8">
                    <h2 id="aircon-service-matcher" className="text-2xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
                        {title ?? 'Which Aircon Service Do You Actually Need?'}
                    </h2>
                    <p className="text-gray-600 leading-relaxed mt-4 max-w-2xl mx-auto">
                        {intro ?? <>You do not need to diagnose the fault yourself. Start with what you are noticing,
                        and we will point you to the right aircon service in Singapore &mdash; along with the
                        likely cause, so you know why it is being recommended.</>}
                    </p>
                </div>

                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                    {/* Mobile: stacked cards so the cause text gets the full viewport width */}
                    <ul className="md:hidden divide-y divide-gray-100">
                        {rows.map((row) => (
                            <li key={row.symptom} className="p-5">
                                <p className="font-semibold text-gray-900 text-sm leading-snug">{row.symptom}</p>
                                <p className="text-sm text-gray-600 leading-relaxed mt-2">{row.reason}</p>
                                <Link
                                    href={row.href}
                                    prefetch={false}
                                    className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-orange-600 bg-orange-50 border border-orange-100 px-3 py-1.5 rounded-full"
                                >
                                    {row.service}
                                    <FaArrowRight className="text-[10px]" />
                                </Link>
                            </li>
                        ))}
                    </ul>

                    {/* Desktop: 3 column table with explicit widths */}
                    <table className="hidden md:table w-full table-fixed text-sm text-left">
                        <caption className="sr-only">
                            {caption ?? 'Aircon symptoms in Singapore matched to the recommended service and likely cause'}
                        </caption>
                        <thead className="bg-gray-50 text-gray-700">
                            <tr>
                                <th scope="col" className="w-[28%] px-4 py-3 font-semibold">What you are noticing</th>
                                <th scope="col" className="w-[22%] px-4 py-3 font-semibold">Service to book</th>
                                <th scope="col" className="w-[50%] px-4 py-3 font-semibold">Likely cause</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.map((row) => (
                                <tr key={row.symptom}>
                                    <th scope="row" className="px-4 py-3 font-medium text-gray-900 align-top">
                                        {row.symptom}
                                    </th>
                                    <td className="px-4 py-3 align-top">
                                        <Link href={row.href} prefetch={false} className="text-orange-600 font-semibold hover:underline">
                                            {row.service}
                                        </Link>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 align-top">{row.reason}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="border-t border-gray-100 p-5 md:p-6">
                        <p className="text-sm text-gray-600">
                            {footnote ?? <><strong className="text-gray-900">Still unsure?</strong> Book a general service.
                            Our technician will tell you on site whether a chemical wash is actually needed
                            &mdash; we do not add it by default.</>}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    )
}
